/**
 * Create / edit a catalogue item (TMMPDM-0019). Remounts when opened. Create POSTs the whole
 * item (a new DRAFT); edit PATCHes only the fields that changed. Kind, currency and period are
 * fixed once created (the PATCH carries none of them).
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { useDiscardChangesGuard, type DiscardChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type {
  AddonAllowedBilling,
  AddonPeriod,
  CatalogView,
  CatalogWriteRequest,
} from '@/api/tmm/types';
import { parseMajor } from '@/routes/rateCards/rateCardInputs';
import { majorFromMinor } from '@/format';
import {
  ALLOWED_BILLING_FALLBACK,
  currencyOptions,
  PERIOD_FALLBACK,
  changedFields,
  invalidMoney,
} from './addonFormat';

const KIND_RE = /^[A-Za-z][A-Za-z0-9_]{0,31}$/;
const PERIODS: AddonPeriod[] = ['ANNUAL', 'MONTHLY'];
const ALLOWED_BILLING: AddonAllowedBilling[] = ['BOTH', 'CARD', 'NET_FROM_PAYOUTS'];
const NAME_MAX = 120;
const DESCRIPTION_MAX = 2000;

interface CatalogItemDialogProps {
  open: boolean;
  /** `null` creates a new item. */
  item: CatalogView | null;
  onClose: () => void;
  onSaved: (item: CatalogView) => void;
}

export function CatalogItemDialog({ open, item, onClose, onSaved }: CatalogItemDialogProps) {
  const { t } = useCopy();
  const discard = useDiscardChangesGuard(
    item?.name ?? t('addons.form.createTitle', 'New add-on'),
    onClose,
  );
  return (
    <>
      <Dialog open={open} onClose={discard.requestClose} fullWidth maxWidth="sm">
        {open ? (
          <CatalogItemFields
            key={item ? `edit-${item.kind}` : 'create'}
            item={item}
            onClose={onClose}
            onSaved={onSaved}
            discard={discard}
          />
        ) : null}
      </Dialog>
      {discard.dialog}
    </>
  );
}

function CatalogItemFields({
  item,
  onClose,
  onSaved,
  discard,
}: Omit<CatalogItemDialogProps, 'open'> & { discard: DiscardChangesGuard }) {
  const { t } = useCopy();
  const theme = useTheme();
  const editing = item !== null;
  const [kind, setKind] = useState(item?.kind ?? '');
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [period, setPeriod] = useState<AddonPeriod>(item?.period ?? 'ANNUAL');
  const [currency, setCurrency] = useState(item?.currency ?? 'USD');
  const initialPrice =
    item?.defaultPriceMinor === undefined
      ? ''
      : majorFromMinor(item.defaultPriceMinor, item.currency);
  const [price, setPrice] = useState(initialPrice);
  const [allowedBilling, setAllowedBilling] = useState<AddonAllowedBilling>(
    item?.allowedBilling ?? 'BOTH',
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // An editing dialog with more than two fields asks before it drops changes (TMM-B5).
  const dirty =
    kind !== (item?.kind ?? '') ||
    name !== (item?.name ?? '') ||
    description !== (item?.description ?? '') ||
    period !== (item?.period ?? 'ANNUAL') ||
    currency !== (item?.currency ?? 'USD') ||
    price !== initialPrice ||
    allowedBilling !== (item?.allowedBilling ?? 'BOTH');
  const { setDirty } = discard;
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  const currencies = currencyOptions(currency);
  const priceMinor = price.trim() === '' ? undefined : parseMajor(price.trim(), currency);

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!editing && !KIND_RE.test(kind.trim())) {
      next.kind = t(
        'addons.form.kindInvalid',
        'A letter, then up to 31 letters, digits or underscores.',
      );
    }
    if (name.trim() === '') next.name = t('addons.form.required', 'Required.');
    else if (name.trim().length > NAME_MAX) {
      next.name = t('addons.form.tooLong', 'At most {{max}} characters.', { max: NAME_MAX });
    }
    if (description.trim() === '') next.description = t('addons.form.required', 'Required.');
    else if (description.trim().length > DESCRIPTION_MAX) {
      next.description = t('addons.form.tooLong', 'At most {{max}} characters.', {
        max: DESCRIPTION_MAX,
      });
    }
    if (price.trim() !== '' && invalidMoney(price)) {
      next.price = t('addons.form.nonNegative', 'Must be zero or greater.');
    }
    if (editing && price.trim() === '' && item.defaultPriceMinor !== undefined) {
      next.price = t(
        'components.cannotClear',
        "This can't be cleared yet. Enter a value, or leave it as it was.",
      );
    }
    return next;
  };

  const submit = async () => {
    const next = validate();
    setFieldErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;
    setSubmitting(true);
    try {
      if (editing) {
        const body = changedFields(item, {
          name: name.trim(),
          description: description.trim(),
          defaultPriceMinor: priceMinor,
          allowedBilling,
        });
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        onSaved(await adminApi.patchAddonCatalog(item.kind, body));
      } else {
        const body: CatalogWriteRequest = {
          kind: kind.trim(),
          name: name.trim(),
          description: description.trim(),
          period,
          currency,
          allowedBilling,
        };
        if (priceMinor !== undefined) body.defaultPriceMinor = priceMinor;
        onSaved(await adminApi.createAddonCatalog(body));
      }
    } catch (err: unknown) {
      if (isValidationFailed(err)) {
        const fields = validationFields(err, t);
        const known = [
          'kind',
          'name',
          'description',
          'currency',
          'defaultPriceMinor',
          'allowedBilling',
          'period',
        ] as const;
        setFieldErrors({
          kind: fields.kind ?? '',
          name: fields.name ?? '',
          description: fields.description ?? '',
          currency: fields.currency ?? '',
          price: fields.defaultPriceMinor ?? '',
          allowedBilling: fields.allowedBilling ?? '',
          period: fields.period ?? '',
        });
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (Object.keys(fields).some((key) => !(known as readonly string[]).includes(key))) {
          setFormError(errorCopy(err, t, t('addons.saveError', 'Could not save the add-on.')));
        }
      } else {
        setFormError(errorCopy(err, t, t('addons.saveError', 'Could not save the add-on.')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {editing
          ? t('addons.form.editTitle', 'Edit add-on')
          : t('addons.form.createTitle', 'New add-on')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('addons.form.kind', 'Kind')}
                value={kind}
                onChange={(event) => setKind(event.target.value)}
                disabled={editing}
                error={Boolean(fieldErrors.kind)}
                helperText={
                  fieldErrors.kind ||
                  (editing
                    ? undefined
                    : t(
                        'addons.form.kindHelp',
                        'A stable key, e.g. BRANDED_APP. Fixed once created.',
                      ))
                }
                required={!editing}
                fullWidth
                slotProps={{
                  htmlInput: { maxLength: 32, style: theme.typography.mono },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('addons.form.name', 'Name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name}
                required
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label={t('addons.form.description', 'Description')}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                error={Boolean(fieldErrors.description)}
                helperText={fieldErrors.description}
                required
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label={t('addons.form.period', 'Period')}
                value={period}
                onChange={(event) => setPeriod(event.target.value as AddonPeriod)}
                disabled={editing}
                error={Boolean(fieldErrors.period)}
                helperText={fieldErrors.period}
                fullWidth
              >
                {PERIODS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`addons.period.${value}`, PERIOD_FALLBACK[value])}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label={t('addons.form.currency', 'Currency')}
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                disabled={editing}
                error={Boolean(fieldErrors.currency)}
                helperText={fieldErrors.currency}
                fullWidth
              >
                {currencies.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('addons.form.defaultPrice', 'Default price')}
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                error={Boolean(fieldErrors.price)}
                helperText={
                  fieldErrors.price ||
                  (priceMinor === undefined || invalidMoney(price)
                    ? t('addons.form.defaultPriceHelp', 'Optional.')
                    : t('addons.form.minorCaption', 'Sends {{minor}} minor units', {
                        minor: priceMinor,
                      }))
                }
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label={t('addons.form.allowedBilling', 'Allowed billing')}
                value={allowedBilling}
                onChange={(event) => setAllowedBilling(event.target.value as AddonAllowedBilling)}
                error={Boolean(fieldErrors.allowedBilling)}
                helperText={fieldErrors.allowedBilling}
                fullWidth
              >
                {ALLOWED_BILLING.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`addons.allowedBilling.${value}`, ALLOWED_BILLING_FALLBACK[value])}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={discard.requestClose}>{t('addons.actions.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {editing ? t('addons.actions.save', 'Save') : t('addons.actions.create', 'Create')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
